import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompetencesSpecifiquesStepRequest, saveAllCompetencesSpecifiquesManagerEvalRequest } from "../../../../redux/actions/performanceActions";
import { asArray, StepState } from "../../Collaborateur/performanceUi";
import "../../Collaborateur/steps/CompetencesSpecifiquesMatrix.css";

const PAGE_SIZE = 10;

const buildFallbackLevels = (evaluations) => {
    const max = Math.max(1, ...evaluations.map((evaluation) => Number(evaluation.niveauMax) || 0));
    return Array.from({ length: max }, (_, index) => ({
        niveau: index + 1,
        nom: `Niveau ${index + 1}`,
    }));
};

export default function CompetencesSpecifiquesManagerStep({ progressId }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.competencesSpecifiques);
    const data = slice.stepByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const evaluations = asArray(data?.evaluations);
    const levels = asArray(data?.niveaux).length > 0 ? asArray(data?.niveaux) : buildFallbackLevels(evaluations);
    const [drafts, setDrafts] = useState({});
    const [page, setPage] = useState(1);
    const [details, setDetails] = useState(null);

    useEffect(() => {
        dispatch(fetchCompetencesSpecifiquesStepRequest(progressId));
    }, [dispatch, progressId]);

    const pageCount = Math.max(1, Math.ceil(evaluations.length / PAGE_SIZE));
    const visibleEvaluations = useMemo(() => evaluations.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [evaluations, page]);
    const updateDraft = (id, patch) => setDrafts((current) => ({ ...current, [id]: { ...(current[id] || {}), ...patch } }));

    const saveAll = () => {
        const items = evaluations.map((evaluation) => {
            const draft = drafts[evaluation.id] || {};
            const commentaire = draft.commentaire ?? evaluation.commentaireManagerEvaluation ?? "";
            return {
                evaluationProgressId: Number(progressId),
                competenceSpecifiqueEvaluationId: evaluation.id,
                niveauManager: draft.niveauManager ?? evaluation.niveauManager ?? evaluation.niveauAutoEvalue,
                commentaire: commentaire.trim(),
            };
        });
        if (items.some((item) => !item.niveauManager)) {
            window.alert("Veuillez choisir un niveau manager pour chaque compétence.");
            return;
        }
        dispatch(saveAllCompetencesSpecifiquesManagerEvalRequest(Number(progressId), items));
    };

    const validatedCount = evaluations.filter((e) => e.niveauManager != null).length;

    return (
        <StepState loading={loading} error={error} empty={!data || evaluations.length === 0}>
            {/* Bandeau statut */}
            <div className={`perf-state perf-state--inline ${data?.managerEvaluationCompleted ? "perf-state--success" : "perf-state--warning"}`}>
                {data?.managerEvaluationCompleted
                    ? "✓ Évaluations validées par le manager"
                    : data?.autoEvaluationCompleted
                        ? "En attente de la validation manager"
                        : "Le manager peut saisir directement ou valider les niveaux du collaborateur"}
            </div>

            {/* Barre de progression */}
            <div className="cg-progress-bar">
                <div className="cg-progress-bar__header">
                    <span className="cg-progress-bar__label">Progression</span>
                    <span className="cg-progress-bar__count">{validatedCount} / {evaluations.length} validées</span>
                </div>
                <div className="cg-progress-track">
                    <div
                        className="cg-progress-fill"
                        style={{ width: evaluations.length ? `${Math.round((validatedCount / evaluations.length) * 100)}%` : "0%" }}
                    />
                </div>
            </div>

            {/* Matrice des compétences */}
            <section className="cs-matrix" style={{ "--cs-level-count": levels.length }}>
                <header className="cs-matrix__header">
                    <strong>Compétence ({evaluations.length} au total)</strong>
                    {levels.map((level) => (
                        <div className="cs-matrix__level-head" key={level.id || level.niveau}>
                            <span>{level.nom}</span>
                            <i />
                        </div>
                    ))}
                    <span>Statut</span>
                </header>

                {visibleEvaluations.map((evaluation) => {
                    const draft = drafts[evaluation.id] || {};
                    const selected = Number(draft.niveauManager ?? evaluation.niveauManager ?? evaluation.niveauAutoEvalue);
                    return (
                        <article className="cs-matrix__row" key={evaluation.id}>
                            <div className="cs-matrix__skill">
                                <span>{evaluation.skillNom || evaluation.skillCode}</span>
                                <button type="button" onClick={() => setDetails(evaluation)}>Détails</button>
                            </div>
                            {levels.map((level) => (
                                <label className="cs-matrix__radio" key={level.id || level.niveau}>
                                    <input
                                        type="radio"
                                        name={`manager-${evaluation.id}`}
                                        checked={selected === Number(level.niveau)}
                                        onChange={() => updateDraft(evaluation.id, { niveauManager: level.niveau })}
                                    />
                                    <span />
                                </label>
                            ))}
                            <span className={`perf-badge ${evaluation.niveauManager != null ? "perf-badge--green" : "perf-badge--orange"}`} style={{ fontSize: 10 }}>
                                {evaluation.niveauManager != null ? "✓ Validée" : "En attente"}
                            </span>
                        </article>
                    );
                })}

                {/* Pagination */}
                <footer className="cs-matrix__pagination">
                    <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>‹</button>
                    {Array.from({ length: pageCount }, (_, index) => (
                        <button
                            key={index + 1}
                            type="button"
                            className={page === index + 1 ? "active" : ""}
                            onClick={() => setPage(index + 1)}
                        >
                            {index + 1}
                        </button>
                    ))}
                    <button type="button" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>›</button>
                    <span>Page {page} / {pageCount} — {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, evaluations.length)} sur {evaluations.length}</span>
                </footer>
            </section>

            {/* Bouton de validation centré en bas */}
            <div className="perf-actions perf-actions--center">
                <button
                    className="perf-btn perf-btn--primary"
                    type="button"
                    disabled={evaluations.length === 0}
                    onClick={saveAll}
                >
                    Enregistrer et valider toutes les compétences
                </button>
            </div>

            {/* Modal détails */}
            {details && (
                <div className="cs-modal-backdrop" role="presentation" onMouseDown={() => setDetails(null)}>
                    <section className="cs-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
                        <header>
                            <h3>{details.skillNom || details.skillCode}</h3>
                            <button type="button" onClick={() => setDetails(null)} title="Fermer">✕</button>
                        </header>
                        <p>{details.jobName || details.skillType || "Compétence spécifique"}</p>
                        <dl className="perf-facts">
                            <div>
                                <dt>Niveau attendu</dt>
                                <dd>{details.niveauAttenduNom || details.niveauAttendu || "—"}</dd>
                            </div>
                            <div>
                                <dt>Note collaborateur</dt>
                                <dd>{details.niveauAutoEvalue ?? "—"}</dd>
                            </div>
                            <div>
                                <dt>Commentaire collaborateur</dt>
                                <dd>{details.commentaireAutoEvaluation || "—"}</dd>
                            </div>
                        </dl>
                        <label className="perf-field" style={{ marginTop: 12 }}>
                            <span>Commentaire manager</span>
                            <textarea
                                value={drafts[details.id]?.commentaire ?? details.commentaireManagerEvaluation ?? ""}
                                onChange={(event) => updateDraft(details.id, { commentaire: event.target.value })}
                                placeholder="Votre commentaire..."
                            />
                        </label>
                        <footer style={{ marginTop: 14 }}>
                            <button className="perf-btn" type="button" onClick={() => setDetails(null)}>Fermer</button>
                        </footer>
                    </section>
                </div>
            )}
        </StepState>
    );
}
